/**
 * SecureBlob Engine Types (aligned with Engine Spec v0.1)
 *
 * Conventions:
 * - All timestamps are epoch ms (number)
 * - Metadata reads (list, stat) are sync; content reads (readText, readBinary) are async
 * - All async methods accept optional AbortSignal
 * - Errors use EngineError / EngineException (§1.5)
 */

// ────────────────────────────────────────────────────────────────
// §1.5 Error Handling
// ────────────────────────────────────────────────────────────────

export type EngineErrorCode =
    | 'NETWORK_ERROR'
    | 'CAS_MISMATCH'
    | 'FORBIDDEN'
    | 'CORRUPT_DATA'
    | 'ABORTED'
    | 'NOT_FOUND'
    | 'COMMIT_INVALID'
    | 'BLOB_SOFT_DELETED'
    | 'OBJECT_MISSING'
    | 'UNSUPPORTED_ALGORITHM'
    | 'UNSUPPORTED_STATE_FORMAT'
    | 'CORRUPTED_MANIFEST'

export interface EngineError {
    readonly code: EngineErrorCode
    readonly message: string
    readonly retryable: boolean
    readonly details?: {
        request?: {
            method: string
            url: string
            headers: Record<string, string>
            body?: unknown
        }
        response?: {
            status: number
            headers: Record<string, string>
            body?: unknown
        }
        [key: string]: unknown
    }
}

export class EngineException extends Error {
    constructor(public readonly error: EngineError) {
        super(error.message)
        this.name = 'EngineException'
    }
}

// ────────────────────────────────────────────────────────────────
// §4.1 File System
// ────────────────────────────────────────────────────────────────

export interface FileStat {
    path: string
    kind: 'file' | 'directory' | 'symlink'
    mime: string
    size: number
    mtime: number              // epoch ms
    ctime: number              // epoch ms — creation time, immutable after creation
    lastWriterKid?: string
    target?: string            // symlink target path (only set when kind === 'symlink')
    meta?: Record<string, unknown>  // extensible metadata bag
}

export type ContentRef =
    | { type: 'inline'; hash: string; compression?: string }
    | { type: 'chunked'; manifestHash: string }
export interface ReadTextOptions {
    /** Character encoding for decoding raw bytes to string.
     * Default: 'utf-8'. Use 'windows-1252' for legacy Windows files.
     * Accepts any encoding supported by TextDecoder. */
    charset?: string
    /** AbortSignal for cancellation. */
    signal?: AbortSignal
}

export type MediaVariantName =
    | 'original'
    | 'thumbnail480'
    | 'thumbnail240'
    | 'preview120'
    | 'transcoded'

export interface MediaVariant {
    readonly variant: MediaVariantName
    readonly mime: string
    readonly size: number
    readonly etag: string
    readonly width?: number
    readonly height?: number
    readonly durationMs?: number
    readonly bitrate?: number
}

export interface MediaMetadata {
    readonly mime: string
    readonly size: number
    readonly width?: number
    readonly height?: number
    readonly durationMs?: number
    readonly bitrate?: number
    readonly codec?: string
    readonly audioChannels?: number
    readonly audioSampleRate?: number
    readonly hasAlpha?: boolean
    readonly title?: string
    readonly artist?: string
}

export interface MediaProcessingOptions {
    readonly thumbnailMaxDimension?: number
    readonly previewMaxDimension?: number
    readonly targetMime?: string
    readonly quality?: number
}

export interface FileSystem {
    // Sync snapshot — in on-demand mode this contains materialized entries.
    // Use listDirAsync() for complete lazy directory reads.
    list(dirPath?: string): FileStat[]
    stat(path: string): FileStat | null

    /**
     * O(k) lazy directory listing — returns only immediate children.
     * Delegates to DirTreeCache.readdirAsync() internally.
     * Use this instead of list() when you only need one directory level.
     */
    listDirAsync(path: string): Promise<FileStat[]>

    /**
     * Streaming variant of listDirAsync — yields FileStat entries one-by-one.
     * Backpressure-friendly for directories with 100k+ children.
     */
    listDirStream(path: string): AsyncIterable<FileStat>

    // ── Read (async — content loaded on-demand) ─────────────────

    /**
     * Read full file content as text.
     * Default charset: 'utf-8'. Pass opts.charset for legacy encodings
     * (e.g. 'windows-1252' for Staircon .exp files).
     */
    readText(path: string, opts?: ReadTextOptions): Promise<string | null>

    /**
     * Read a byte range [offset, offset+length) from a file.
     * V3: only fetches+decrypts chunks that overlap the range.
     */
    readBytes(path: string, opts: { offset: number; length: number }, signal?: AbortSignal): Promise<Uint8Array | null>

    /**
     * Stream file content chunk-by-chunk.
     * V3 optimization: supports seeking via `offset` — skips earlier chunks in O(1).
     * Optional `length` limits total bytes yielded.
     * Optional `chunkSize` re-slices output into uniform chunks.
     *
     * Use cases: FUSE read() with offset, HTTP Range, seekable media, resumable downloads.
     */
    createByteStream(
        path: string,
        opts?: { offset?: number; length?: number; chunkSize?: number },
        signal?: AbortSignal,
    ): AsyncIterable<Uint8Array>

    /** @deprecated Use readBytes or createByteStream instead. */
    readBinary(path: string, signal?: AbortSignal): Promise<Uint8Array | null>

    // ── Write (sync — updates VFS, triggers async commit+sync) ───

    /** Write full text content. */
    writeText(path: string, content: string): void
    /** Write full binary content. */
    writeBytes(path: string, bytes: Uint8Array): void

    /**
     * Partial write: overwrite bytes at a specific offset.
     * V3: leverages CDC dedup — unchanged chunks are reused (not re-encrypted).
     * Only the chunks overlapping the patch region are re-encrypted.
     */
    patchBytes(path: string, offset: number, data: Uint8Array, signal?: AbortSignal): Promise<void>

    /**
     * Append bytes to the end of a file.
     * V3: CDC chunk stability means existing chunks are reused via dedup.
     * Only the last chunk (boundary) and new chunks are encrypted.
     */
    appendBytes(path: string, data: Uint8Array, signal?: AbortSignal): Promise<void>

    /** Delete a file. Returns true if the file existed. */
    delete(path: string): boolean
    /** Rename/move a file. Returns true if the source existed. */
    rename(oldPath: string, newPath: string): boolean
    /** Create a directory (idempotent). */
    mkdir(path: string): void

    /**
     * Create a symbolic link.
     * V3: symlinks are first-class entries in the Merkle directory tree.
     */
    symlink(path: string, target: string): void

    /**
     * Update file metadata without re-writing content.
     * V3: only the directory entry is re-encrypted, not the content.
     */
    setMeta(path: string, meta: Record<string, unknown>): void

    /**
     * §5.1.1 Batch import: write N files as a single atomic commit.
     * Phase 1: encrypt each file's content (onProgress fires per file).
     * Phase 2: create ONE commit with all files.
     *
     * Cancellation via opts.signal:
     *   signal.reason === 'keep'    → commit files processed so far (partial import)
     *   signal.reason === 'discard' → rollback VFS, no commit, throw AbortError
     *   signal.reason === undefined → same as 'discard'
     */
    importFiles?(
        files: ImportFileEntry[],
        opts?: ImportFilesOptions,
    ): Promise<ImportFilesResult>
}

export interface ImportFileEntry {
    path: string
    content: string | Uint8Array
    mime?: string  // default: string → 'text/plain', Uint8Array → 'application/octet-stream'
}

export interface ImportFilesProgress {
    current: number       // 1-based index of file just encrypted
    total: number         // total files in batch
    path: string          // path of current file
    replaced: boolean     // true if overwrites existing file
    replacedTotal: number // running total of replaced files so far
}

export interface ImportFilesOptions {
    signal?: AbortSignal
    onProgress?: (progress: ImportFilesProgress) => void
    /** Called when import transitions between phases: 'encrypting' → 'committing' */
    onPhase?: (phase: 'encrypting' | 'committing') => void
}

export interface ImportFilesResult {
    created: number       // files written (includes replacements)
    replaced: number      // how many were overwrites
    paths: string[]       // all paths written
    aborted?: boolean     // true if partial import (Cancel & Keep)
}

// ────────────────────────────────────────────────────────────────
// §10 History, Diff & Blame
// ────────────────────────────────────────────────────────────────

export interface AffectedFile {
    path: string
    changeType: 'added' | 'modified' | 'deleted'
    additions?: number
    deletions?: number
}

export interface CommitSummary {
    commitHash: string
    parents: string[]
    time?: number               // epoch ms
    writerKid: string
    writerName?: string
    writerEmoji?: string
    message?: string
    affectedFiles: AffectedFile[]
}

export interface BlameLine {
    lineNumber: number
    text: string
    commitHash: string | null
    writerKid: string | null
    writerName?: string
    writerEmoji?: string
    timestamp?: number | null  // epoch ms
    local?: boolean
}

export interface DiffHunk {
    type: 'context' | 'addition' | 'deletion'
    lineOld?: number
    lineNew?: number
    text: string
}

/** Shared diff-view state passed between App → EditorView */
export interface DiffState {
    path: string
    commitHash: string
    commitMeta: {
        id: string
        writerEmoji: string
        writerName: string
        timestamp?: number  // epoch ms, undefined for old commits without time
    }
}

export interface History {
    // Sync — reads from cached commitIndex
    list(opts?: { limit?: number; cursor?: string }): CommitSummary[]
    listForFile(path: string, opts?: { limit?: number; cursor?: string }): CommitSummary[]

    // Async — require object fetching / decryption
    getCommit(hash: string, signal?: AbortSignal): Promise<CommitSummary>
    blame(path: string, signal?: AbortSignal): Promise<BlameLine[]>
    listDirAtCommit(path: string, commitHash: string): Promise<FileStat[]>
    readTextAtCommit(path: string, commitHash: string, opts?: ReadTextOptions): Promise<string | null>
    readBytesAtCommit(path: string, commitHash: string, signal?: AbortSignal): Promise<Uint8Array | null>
    diffText(path: string, fromCommit: string, toCommit: string, signal?: AbortSignal): Promise<DiffHunk[]>
}

// ────────────────────────────────────────────────────────────────
// §3.2 Sync State
// ────────────────────────────────────────────────────────────────

export interface SyncLogEntry {
    readonly id: number
    readonly timestamp: number
    readonly type: 'sync-start' | 'sync-ok' | 'sync-error' | 'pull-ok' | 'pull-skip'
    readonly durationMs?: number
    readonly error?: EngineError
    readonly detail?: string
    readonly filesAffected?: number
    readonly affectedPaths?: readonly string[]
}

export interface SyncProgress {
    readonly phase: 'push' | 'cas' | 'pull' | 'merge'
    readonly objectsUploaded: number
    readonly objectsTotal: number
    readonly bytesUploaded: number
    readonly bytesTotal: number
}

export type WorkspaceSyncPolicy = 'on-demand' | 'auto' | 'paused'

export interface SyncState {
    readonly syncing: boolean
    readonly online: boolean
    readonly connectivity: 'connected' | 'offline' | 'unreachable'
    readonly synced: boolean
    readonly lastSyncAt: number               // epoch ms
    readonly pendingChanges: number
    readonly lastSyncError?: EngineError
    readonly tombstoned?: boolean             // server confirmed workspace deleted (410 BLOB_SOFT_DELETED)
    readonly conflictPaths?: string[]
    readonly localHead?: string
    readonly remoteHead?: string
    readonly syncLog: readonly SyncLogEntry[]
    readonly syncProgress?: SyncProgress      // §7.8: populated during active sync
    readonly syncPolicy?: WorkspaceSyncPolicy  // §413: on-demand metadata sync, auto offline materialization, or paused
    readonly syncPaused?: boolean             // §7.8: true when user paused sync
}

// ────────────────────────────────────────────────────────────────
// §9 Sharing
// ────────────────────────────────────────────────────────────────

export interface ShareLink {
    url: string
    mode: 'read' | 'readwrite'
    writerKid?: string
}

export interface Sharing {
    createShareLink(mode: 'read' | 'readwrite', path?: string, viewMode?: 'edit' | 'preview'): ShareLink
    /**
     * Async invite creation via server control-plane (Spec §13).
     * Generates invite seeds, calls POST /invites, returns encoded invite v2
     * suitable for `#invite=` URL or `sb-invite:` clipboard format.
     */
    createInviteLink(mode: 'read' | 'readwrite', path?: string, viewMode?: 'edit' | 'preview'): Promise<ShareLink>
}

// ────────────────────────────────────────────────────────────────
// §17 Presence (E2E encrypted via WebSocket)
// ────────────────────────────────────────────────────────────────

export interface PresenceEntry {
    kid: string
    name?: string
    emoji?: string
    activeFile?: string
    device?: 'web' | 'mcp' | 'desktop'
    isSelf: boolean
}

export interface PresenceManager {
    startHeartbeat(): void
    stopHeartbeat(): void
    getViewers(): PresenceEntry[]
    on(event: 'presence:changed', fn: (viewers: PresenceEntry[]) => void): () => void
}

// ────────────────────────────────────────────────────────────────
// §18 Writer Management
// ────────────────────────────────────────────────────────────────

export interface WriterInfo {
    kid: string
    pk: string
    name?: string
    emoji?: string
    role: 'owner' | 'editor'
}

// ────────────────────────────────────────────────────────────────
// §3.2 Events
// ────────────────────────────────────────────────────────────────

export type WorkspaceEventType =
    | 'state:changed'
    | 'fs:changed'
    | 'fs:external-change'
    | 'history:changed'

export interface FsChangeEvent {
    paths: string[]
}

export interface FsExternalChangeEvent {
    paths: string[]
    writerKid?: string
    source: 'sync' | 'merge' | 'fast-forward' | 'adopt'
}

export interface HistoryChangedEvent {
    newCommits: string[]      // commitHashes that arrived
}

export type WorkspaceEventPayload = {
    'state:changed': SyncState
    'fs:changed': FsChangeEvent
    'fs:external-change': FsExternalChangeEvent
    'history:changed': HistoryChangedEvent
}

// ────────────────────────────────────────────────────────────────
// §3.1 Workspace
// ────────────────────────────────────────────────────────────────

export interface Workspace {
    readonly blobId: string
    readonly canWrite: boolean
    readonly label: string
    readonly emoji: string

    state: SyncState
    fs: FileSystem
    history: History
    sharing: Sharing
    presence: PresenceManager

    on<E extends WorkspaceEventType>(event: E, fn: (payload: WorkspaceEventPayload[E]) => void): () => void
    off<E extends WorkspaceEventType>(event: E, fn: (payload: WorkspaceEventPayload[E]) => void): void
    /**
     * Trigger an immediate on-demand sync without forcing full workspace
     * materialization. Use this for lazy clients and component jobs that need
     * remote visibility for touched paths/lanes while preserving on-demand IO.
     */
    syncOnDemand?(): Promise<void>
    syncNow(): Promise<void>
    getSyncPolicy(): WorkspaceSyncPolicy
    setSyncPolicy(policy: WorkspaceSyncPolicy): void
    prefetchContent(paths?: string | readonly string[]): Promise<void>
    pauseSync(): void       // §7.8: abort in-flight push, block future syncs
    resumeSync(): void      // §7.8: unblock and trigger immediate sync
    close(): void
    commitTombstone(): Promise<void>
    /** §5.3.1: Create an empty genesis commit to establish writer identity. No-op if commits exist. */
    createGenesisCommit(): Promise<void>

    // Writer management
    getWriters(): WriterInfo[]
    removeWriter(kid: string): Promise<void>
    /** Resolve a writerKid to display name/emoji. Returns {} if unknown. */
    resolveWriterName(kid: string): { name?: string; emoji?: string }

    /** Acquire an opaque, resource-scoped lease within this workspace blob. */
    acquireResourceLease(
        resourceName: string,
        options: { mode?: 0 | 1 | 2; holderId: string; ttlSeconds?: number },
    ): Promise<WorkspaceResourceLease | null>
    /** Refresh a previously acquired workspace resource lease. */
    refreshResourceLease(leaseId: string, ttlSeconds?: number): Promise<WorkspaceResourceLease | null>
    /** Release a previously acquired workspace resource lease. */
    releaseResourceLease(leaseId: string): Promise<boolean>

    /** Test helper: simulate remote edit */
    simulateExternalEdit?(path: string, newContent: string, writerKid?: string): void
}

export interface WorkspaceResourceLease {
    leaseId: string
    resourceId: string
    mode: 0 | 1 | 2
    expiresAt: string
}

export type SqliteBackgroundSyncMode = 'workspace-policy' | 'manual'

export interface OpenSqliteOptions {
    signal?: AbortSignal
    /** Override the SDK-level SQLite backend for this open call. */
    backend?: 'sqlite3mt' | 'sqlite3mtlight'
    syncIntervalMs?: number
    /**
     * Controls session-owned background sqlite3mt sync for this handle.
     * With the default `workspace-policy`, workspace policy `on-demand` keeps
     * sqlite lanes readable through local/page-fault IO without starting
     * background sync; `auto` starts lane sync/polling, and `paused`/`manual`
     * keep sync stopped until an explicit barrier such as syncNow/flushRemote.
     * `manual` is for bounded server/component jobs that explicitly call
     * flushRemote() at deterministic checkpoints.
     */
    backgroundSync?: SqliteBackgroundSyncMode
}

// ────────────────────────────────────────────────────────────────
// §13 EngineManager
// ────────────────────────────────────────────────────────────────

export interface WorkspaceEntry {
    blobId: string
    label: string
    emoji: string
    apiBaseUrl: string
    capability: string        // encoded capability
    trustLevel?: 'trusted' | 'untrusted'
    agentSecretsAllowed?: boolean
    canWrite?: boolean
    notifications?: { push?: boolean; inApp?: boolean }
}


// MCP connection entry — stub for EngineManager interface.
// Full type lives in webapp's mcp/types.ts.
export interface McpConnectionEntry {
    configBlobId: string
    readKey: string
    readerSeed?: string
    writerSeed: string
    label: string
    ownerKid: string
    createdAt: string
    workspaceBlobIds: string[]
    mcpReaderSeeds?: Record<string, string>
    mcpWriterSeeds?: Record<string, string>
    purpose?: string
    parentConfigBlobId?: string
    linkedConfigBlobIds?: string[]
    linkedWorkspaceRefs?: string[]
}

export interface WorkspaceInfo {
    blobId: string
    label: string
    emoji: string
    state: SyncState
}

/** Key entry in the identity keyring (§9.5) */
export interface CanonicalKeyRingEntry {
    kid: string
    pk: string
    source: 'primary' | 'imported' | 'invite'
    addedAt: string           // ISO
    blobId?: string           // optional binding to a specific blob
}

/** The core material of a user identity (§9.5) */
export interface CanonicalIdentity {
    v: number
    primarySeed: string       // only the primary key seed is stored in the core object
    keyRing: CanonicalKeyRingEntry[]
}

export interface UserIdentity {
    kid: string
    name: string
    emoji: string
    keyRing?: CanonicalKeyRingEntry[]
    canonical?: CanonicalIdentity
}

export interface ProfileEntry {
    kid: string
    name?: string
    emoji?: string
}


/** Device info returned from the trusted-device server list endpoint. §05 §10. */
export interface TrustedDeviceInfo {
    deviceId: string
    label?: string
    createdAt: string
    lastSeenAt: string
    revokedAt?: string
}

export interface IdentityProvider {
    isUnlocked(): boolean
    unlock(pin?: string): Promise<void>
    lock(): void
    hasIdentity(): boolean
    getIdentity(): UserIdentity | null
    createIdentity(params?: { name?: string; emoji?: string; pin?: string; webAuthnOnly?: boolean }): Promise<UserIdentity>
    importIdentity(payload: string): Promise<UserIdentity>
    exportIdentity(): string
    /** Attempt vault auto-unlock (cached PIN only — skips WebAuthn). */
    tryAutoUnlock(): Promise<boolean>
    /** Explicitly unlock a WebAuthn vault via browser assertion prompt. User-initiated only. */
    unlockWithWebAuthn(): Promise<boolean>
    /** Permanently delete vault record + lock. Irreversible. */
    deleteVault(): Promise<void>
    /** Read the unlock method without unlocking. Returns null if no vault. */
    getVaultUnlockMethod(): Promise<'webauthn' | 'webauthn-prf' | 'pin' | null>
    /** Check if a vault record exists in storage (without unlocking). */
    hasVaultRecord(): Promise<boolean>
    /** Update profile name/emoji in the vault (must be unlocked). */
    updateProfile(profile: { name?: string; emoji?: string }): Promise<void>
    /** Attempt to recover identity from server using discoverable WebAuthn credentials. */
    recoverFromServer(): Promise<boolean>

    // ─── Trusted Device management (§05 §10, §148) ──────────────────────
    /** Enroll this browser as a trusted device. Requires unlocked vault. §05 §10.4 */
    trustDevice(label?: string): Promise<{ deviceId: string }>
    /** List all trusted devices for the current identity (from server). §05 §10.6 */
    getTrustedDevices(): Promise<TrustedDeviceInfo[]>
    /** Revoke a specific trusted device. §05 §10.6 */
    revokeTrustedDevice(deviceId: string): Promise<void>
    /** Revoke ALL trusted devices for the current identity. §05 §10.6 */
    revokeAllTrustedDevices(): Promise<void>
    /** Check if the current device is enrolled as a trusted device. */
    isCurrentDeviceTrusted(): Promise<boolean>
}

export type EngineManagerEventType =
    | 'workspace:switched'
    | 'workspace:sync-status'
    | 'workspace:deleted'
    | 'registry:changed'
    | 'registry:sync-status'
    | 'registry:workspace-added'
    | 'registry:workspace-removed'
    | 'identity:changed'

export type EngineManagerEventPayload = {
    'workspace:switched': Workspace | null
    'workspace:sync-status': { blobId: string; state: SyncState }
    'workspace:deleted': { blobId: string; deletedByKid?: string }
    'registry:changed': undefined
    'registry:sync-status': { state: 'syncing' | 'synced' | 'error'; isInitial: boolean; error?: string }
    'registry:workspace-added': { blobId: string; label: string; emoji: string }
    'registry:workspace-removed': { blobId: string }
    'identity:changed': undefined
}

export interface EngineManager {
    // Workspace lifecycle
    openWorkspace(params: {
        apiBaseUrl: string
        blobId: string
        capability: string
        canWrite?: boolean
        initialPath?: string
        conflictResolution?: 'auto' | 'manual'
    }): Promise<Workspace>
    activateWorkspace(blobId: string): Promise<Workspace>
    suspendWorkspace(blobId: string): void
    getActiveWorkspace(): Workspace | null
    getSuspendedWorkspaces(): WorkspaceInfo[]

    // Identity
    readonly identity: IdentityProvider

    // Deprecated compatibility wrappers
    hasIdentity(): boolean
    getIdentity(): UserIdentity | null
    createIdentity(params?: { name?: string; emoji?: string; pin?: string; webAuthnOnly?: boolean }): Promise<UserIdentity | null>
    importIdentity(payload: string): Promise<UserIdentity>
    exportIdentity(): Promise<string>

    /** Lock vault (keeps data — can re-unlock). */
    logoutIdentity(): void
    /** Permanently delete identity vault. */
    deleteIdentity(): Promise<void>
    /** Attempt auto-unlock on startup (PIN only, no browser modal). */
    tryAutoUnlock(): Promise<boolean>
    /** Explicitly unlock a WebAuthn vault (shows browser modal). */
    unlockWithWebAuthn(): Promise<boolean>
    /** Update profile name/emoji in the vault (must be unlocked). */
    updateProfile(profile: { name?: string; emoji?: string }): Promise<void>
    /** Attempt to recover identity from server (discoverable WebAuthn). */
    recoverFromServer(): Promise<boolean>

    // ─── Trusted Device management (§05 §10, §148) ──────────────────────
    /** Enroll this browser as a trusted device. */
    trustDevice(label?: string): Promise<{ deviceId: string }>
    /** List all trusted devices for the current identity (from server). */
    getTrustedDevices(): Promise<TrustedDeviceInfo[]>
    /** Revoke a specific trusted device. */
    revokeTrustedDevice(deviceId: string): Promise<void>
    /** Revoke ALL trusted devices for the current identity. */
    revokeAllTrustedDevices(): Promise<void>
    /** Check if the current device is enrolled as a trusted device. */
    isCurrentDeviceTrusted(): Promise<boolean>

    // Registry
    getRegistry(): WorkspaceEntry[]
    addToRegistry(entry: WorkspaceEntry): void
    removeFromRegistry(blobId: string): void
    updateRegistryEntry(blobId: string, updates: Partial<Pick<WorkspaceEntry, 'label' | 'emoji' | 'notifications'>>): void
    /** Commit a tombstone to the workspace (marking it deleted for all peers) and remove from registry. */
    deleteWorkspace(blobId: string): Promise<void>
    /** Leave a workspace without tombstoning — removes from registry so the user can rejoin via share link. */
    leaveWorkspace(blobId: string): void
    /** Check if a blobId was previously tombstoned (permanently deleted). */
    isTombstoned(blobId: string): boolean
    /** Remove a blobId from the tombstone deny-list (used for deterministic workspaces like Home). */
    removeTombstone(blobId: string): void

    // MCP Connections (stored in registry, spec §07 §3.4)
    getMcpConnections(): McpConnectionEntry[]
    addMcpConnection(entry: McpConnectionEntry): void
    removeMcpConnection(configBlobId: string): void
    updateMcpConnection(configBlobId: string, updates: Partial<McpConnectionEntry>): void

    // Directory
    lookupKid(kid: string): ProfileEntry | null

    // Events
    on<E extends EngineManagerEventType>(event: E, fn: (payload: EngineManagerEventPayload[E]) => void): () => void

    // §7.6.1 Unified manual sync — sync workspace + registry + MCP config-blobs
    syncAllNow(): Promise<void>

    dispose(): void
}

// ────────────────────────────────────────────────────────────────
// §1.1–1.4 Provider Interfaces
// ────────────────────────────────────────────────────────────────

export type HeadUpdateResult =
    | { ok: true }
    | { ok: false; code: 'CAS_MISMATCH'; remoteHead: string }
    | { ok: false; code: 'FORBIDDEN' | 'NOT_FOUND' | 'NETWORK_ERROR' | 'COMMIT_INVALID' | 'OBJECT_MISSING'; message: string }

/** §3.3: Rich result from getHeads — includes optional prefetchHints for cold-start optimization. */
export interface HeadsResult {
    heads: string[]
    prefetchHints?: string[]
}

export interface RemoteStore {
    getHeads(blobId: string, signal?: AbortSignal, capability?: string, lane?: string): Promise<HeadsResult>
    getObject(hash: string, signal?: AbortSignal, capability?: string, blobId?: string): Promise<Uint8Array | null>
    getObjectsBatch(hashes: string[], signal?: AbortSignal, capability?: string, blobId?: string): Promise<Map<string, Uint8Array>>
    putObject(hash: string, bytes: Uint8Array, signal?: AbortSignal, capability?: string, blobId?: string): Promise<void>
    putObjectsBatch(entries: Array<{ hash: string; bytes: Uint8Array }>, signal?: AbortSignal, capability?: string, blobId?: string): Promise<void>
    updateHead(blobId: string, base: string[], newHead: string, signal?: AbortSignal, capability?: string, referencedObjects?: string[], prefetchHints?: string[], lane?: string): Promise<HeadUpdateResult>
    softDeleteBlob(blobId: string, signal?: AbortSignal, capability?: string): Promise<void>
}

export interface OutboxEntry {
    hash: string
    bytes: Uint8Array
    kind: 'object' | 'commit'
}

export interface LocalStore {
    getLocalHead(blobId: string): Promise<string | null>
    setLocalHead(blobId: string, hash: string): Promise<void>
    getRemoteHead(blobId: string): Promise<string | null>
    setRemoteHead(blobId: string, hash: string): Promise<void>
    getObject(hash: string): Promise<Uint8Array | null>
    putObject(hash: string, bytes: Uint8Array): Promise<void>
    hasObject(hash: string): Promise<boolean>
    getOutbox(blobId: string): Promise<OutboxEntry[]>
    addToOutbox(blobId: string, entry: OutboxEntry): Promise<void>
    clearOutbox(blobId: string, hashes: string[]): Promise<void>
    dispose(): Promise<void>
}

export interface CryptoProvider {
    sha256(data: Uint8Array): Promise<string>
    /** Raw SHA-256 digest as 32 bytes (not hex). Used for HMAC nonce input. */
    sha256Bytes(data: Uint8Array): Promise<Uint8Array>
    encrypt(key: Uint8Array, plaintext: Uint8Array, aad?: Uint8Array): Promise<Uint8Array>
    /** Content-addressed encryption: HMAC-derived deterministic nonce.
     *  Internally: nonceKey = HKDF(key, 'nonce-derivation'), nonce = HMAC(nonceKey, SHA-256(plaintext))[0:24].
     *  Same (key, plaintext, aad) → same ciphertext → same hash → natural dedup. */
    encryptDeterministic(key: Uint8Array, plaintext: Uint8Array, aad: Uint8Array): Promise<Uint8Array>
    decrypt(key: Uint8Array, ciphertext: Uint8Array, aad?: Uint8Array): Promise<Uint8Array>
    /** HMAC-SHA256. Returns 32-byte MAC. */
    hmac(key: Uint8Array, data: Uint8Array): Promise<Uint8Array>
    sign(seed: Uint8Array, message: Uint8Array): Promise<Uint8Array>
    verify(pk: Uint8Array, message: Uint8Array, sig: Uint8Array): Promise<boolean>
    hkdf(ikm: Uint8Array, salt: Uint8Array, info: Uint8Array, len: number): Promise<Uint8Array>
    randomBytes(length: number): Uint8Array
}

export interface SyncScheduler {
    scheduleDebouncedSync(callback: () => Promise<void>): void
    startPolling(callback: () => Promise<void>, intervalMs: number): void
    stopPolling(): void
    setMode(mode: 'active' | 'suspended'): void
    setNotificationSource(source: 'polling' | 'websocket'): void
    dispose(): void
}
